/**
 * Pure helpers for SimpleHtmlEditor. No React, no DOM side-effects beyond
 * a throwaway parsing element — so they are unit-testable in jsdom.
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'ul', 'ol', 'li', 'a', 'br', 'div', 'p'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

/**
 * Count the visible characters in an HTML string: tags are stripped and
 * entities are decoded, so "<b>hi</b>" counts as 2 and "a&amp;b" as 3.
 * Whitespace is counted, matching how a user perceives the description.
 */
export function countVisibleChars(html: string): number {
    if (!html) return 0;
    return parseHtml(html).body.textContent?.length ?? 0;
}

/**
 * Parse an HTML string into a detached Document. DOMParser does NOT execute
 * scripts or attach anything to the live DOM, so this is safe for measuring
 * untrusted editor output (no XSS surface).
 */
function parseHtml(html: string): Document {
    return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * Convert Chrome's Enter-created <div> and Firefox's <p> line-break elements
 * to <br> tags so the output only uses tags from the ALLOWED_TAGS whitelist.
 * Each block element becomes its inline content followed by <br>; the very
 * last one gets no trailing <br> (avoiding an unwanted blank line on render).
 */
export function normalizeDivLineBreaks(html: string): string {
    if (!html) return html;
    const doc = parseHtml(html);
    const body = doc.body;
    const blocks = Array.from(body.querySelectorAll('div, p'));
    for (const block of blocks) {
        while (block.firstChild) {
            block.parentNode?.insertBefore(block.firstChild, block);
        }
        if (block.nextSibling) {
            block.parentNode?.insertBefore(doc.createElement('br'), block);
        }
        block.parentNode?.removeChild(block);
    }
    return body.innerHTML;
}

/**
 * Truncate HTML so its visible text is at most `max` characters. Text is cut
 * in place, trailing siblings are dropped, and the DOM serializer guarantees
 * ancestor tags stay balanced — so "<b>hello</b>" @ 3 becomes "<b>hel</b>".
 */
export function truncateHtmlToVisibleChars(html: string, max: number): string {
    if (!html || max <= 0) return '';
    const body = parseHtml(html).body;
    trimTo(body, { remaining: max });
    return body.innerHTML;
}

function trimTo(node: Node, budget: { remaining: number }): void {
    // Iterate a static snapshot so removing children mid-walk is safe.
    for (const child of Array.from(node.childNodes)) {
        if (budget.remaining <= 0) {
            node.removeChild(child);
        } else if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent ?? '';
            if (text.length >= budget.remaining) {
                child.textContent = text.slice(0, budget.remaining);
                budget.remaining = 0;
            } else {
                budget.remaining -= text.length;
            }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            trimTo(child, budget);
        }
    }
}

/**
 * Sanitize untrusted HTML for safe rendering to students. Only the
 * formatting/list/link whitelist survives; scripts, inline styles, and
 * dangerous URI schemes (e.g. javascript:) are stripped by DOMPurify. Every
 * surviving link is then forced to open in a new tab with rel=noopener.
 */
export function sanitizeBannerHtml(html: string): string {
    const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
    const body = parseHtml(sanitized).body;
    body.querySelectorAll('a').forEach((a) => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
    });
    return body.innerHTML;
}

const URL_PATTERN = /https?:\/\/[^\s<]+/gi;

/**
 * Turn bare http(s) URLs in text into safe links. Walks text nodes with a
 * TreeWalker and skips any that sit inside an existing <a>, so URLs that are
 * already linked are never double-wrapped.
 */
export function linkifyUrls(html: string): string {
    if (!html) return '';
    const body = parseHtml(html).body;

    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (node.parentElement?.closest('a')) return NodeFilter.FILTER_REJECT;
            return URL_PATTERN.test(node.nodeValue ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
    });

    const targets: Text[] = [];
    let current = walker.nextNode();
    while (current) {
        targets.push(current as Text);
        current = walker.nextNode();
    }

    for (const textNode of targets) {
        textNode.parentNode?.replaceChild(buildLinkifiedFragment(textNode.nodeValue ?? ''), textNode);
    }
    return body.innerHTML;
}

function buildLinkifiedFragment(text: string): DocumentFragment {
    const fragment = document.createDocumentFragment();
    const matcher = new RegExp(URL_PATTERN.source, 'gi');
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = matcher.exec(text)) !== null) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        const link = document.createElement('a');
        link.href = match[0];
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = match[0];
        fragment.appendChild(link);
        lastIndex = match.index + match[0].length;
    }
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    return fragment;
}
